import { inject, Injectable, Injector, NgZone } from '@angular/core';
import posthog from 'posthog-js';
import { environment } from '../../../environments/environment';
import { ThemeService } from '../_theme/theme.service';
import { EventTrackerService } from './eventTracker.service';
import { isLocalhost } from '../../../main';

export interface PerformanceTransaction {
  name: string;
  startMark: string;
  endMark?: string;
}

@Injectable({ providedIn: 'root' })
export class PosthogService {
  private injector = inject(Injector);
  private ngZone = inject(NgZone);
  private themeService = inject(ThemeService);

  private _eventTrackerService?: EventTrackerService;
  version: string = this.themeService.getLocalVersion(true);
  versionDate: string = this.themeService.getLocalVersionDate();

  constructor() {
    if (environment.postHogEnabled && !isLocalhost) {
      this.initPosthogBase();
    }
  }

  private get eventTrackerService(): EventTrackerService {
    if (!this._eventTrackerService) {
      this._eventTrackerService = this.injector.get(EventTrackerService);
    }
    return this._eventTrackerService;
  }

  /** Initializes PostHog with minimal config for error tracking (no cookies, no analytics) */
  private initPosthogBase(): void {
    this.ngZone.runOutsideAngular(() => {
      try {
        posthog.init(environment.postHogKey, {
          api_host: environment.eventTrackingEndpoint || 'https://app.posthog.com',
          ui_host: 'https://eu.i.posthog.com',
          autocapture: false, // disable analytics
          capture_pageview: false, // disable pageview tracking
          disable_session_recording: true, // disable session recording
        });

        // Register global app metadata (automatically added to all events)
        posthog.register({
          package: environment.themeConfig.brand.toLowerCase() || 'xFarm',
          version: this.version || '0.0.unknown',
          releaseDate: this.versionDate || 'unknown',
          environment: environment.themeConfig.textName.toUpperCase(),
        });
        console.log('[PostHog] base initialized (error tracking only mode)');
      } catch (error) {
        console.error('[PostHog] base init error:', error);
      }
    });
  }

  /** Called when user accepts cookies */
  enableAnalytics(): void {
    if (!environment.postHogEnabled) return;

    posthog.opt_in_capturing();
    posthog.config.autocapture = true;
    console.log('[PostHog] analytics enabled');
  }

  /** Called when user refuses cookies */
  disableAnalytics(): void {
    if (!environment.postHogEnabled) return;

    posthog.opt_out_capturing();
    posthog.config.autocapture = false;
    console.log('[PostHog] analytics disabled');
  }

  /** Identifies the user in PostHog and enables analytics if not yet active */
  identifyUser(userId: string, properties?: Record<string, any>): void {
    if (!environment.postHogEnabled) return;

    this.eventTrackerService.eventTrackingActivated.set(true);
    try {
      this.enableAnalytics(); // ensure opt-in and autocapture
      posthog.identify(userId, properties);
      console.log('[PostHog] user identified:', userId, properties);
    } catch (error) {
      console.error('[PostHog] identify error:', error);
    }
  }

  /** Description - Reset the PostHog connection */
  reset(): void {
    if (!environment.postHogEnabled) return;

    try {
      posthog.reset();
      console.log('[PostHog] user reset');
    } catch (error) {
      console.error('[PostHog] reset error:', error);
    }
  }

  /** Start a custom performance transaction */
  startCustomTransaction(name: string): PerformanceTransaction {
    const startMark = `${name}-start`;
    performance.mark(startMark);

    console.groupCollapsed(`[Performance] ${name}`);
    console.log('Started transaction at:', new Date().toISOString());
    console.groupEnd();

    return { name, startMark };
  }

  /** End a custom performance transaction and send to PostHog if enabled */
  endCustomTransaction(transaction: PerformanceTransaction): void {
    if (!transaction) return;

    const endMark = `${transaction.name}-end`;
    transaction.endMark = endMark;
    performance.mark(endMark);

    const measureName = `${transaction.name}-measure`;
    performance.measure(measureName, transaction.startMark, endMark);
    const measure = performance.getEntriesByName(measureName)[0];

    console.groupCollapsed(`[Performance] ${transaction.name}`);
    console.log('Duration:', `${measure.duration.toFixed(2)} ms`);
    console.log('Start mark:', transaction.startMark);
    console.log('End mark:', endMark);
    console.groupEnd();

    if (environment.postHogEnabled && posthog.config?.autocapture) {
      this.captureEvent('performance_measure', {
        name: transaction.name,
        duration: measure.duration,
        page: window.location.pathname,
        timestamp: new Date().toISOString(),
      });
    }

    performance.clearMarks(transaction.startMark);
    performance.clearMarks(endMark);
    performance.clearMeasures(measureName);
  }

  /** Captures a custom event with optional properties */
  captureEvent(eventName: string, properties?: Record<string, any>): void {
    if (!environment.postHogEnabled) return;

    try {
      posthog.capture(eventName, properties);
      console.log('[PostHog] event captured:', eventName, properties);
    } catch (error) {
      console.error('[PostHog] capture error:', error);
    }
  }
}
